// Voice calling settings: how long a number stays open, ad frequency, longest call.
import React, { useState } from 'react';
import { toast } from 'sonner';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useVoiceRefresh, useVoiceSettingsQuery, useVoiceSettingsUpdateMutation } from '@/client/api/voice/voice.query';
import { VoiceSettings } from '@/client/types/voice';
import { Card, FormDialog, INPUT_CLASS, PageShell } from '../Cloud/cloudUi';
import { AccessDenied, ErrorNotice, FetchingChip, LoadingNote, VOICE_BASE, fmtWhen, useVoicePerms } from './voiceUi';

type Draft = { hold: string; ads: string; max: string };
const toDraft = (s: VoiceSettings): Draft => ({
  hold: String(s.hold_seconds),
  ads: String(s.ad_frequency_percent),
  max: String(s.max_call_minutes),
});
const inRange = (v: string, lo: number, hi: number) => {
  const n = Number(v);
  return v !== '' && Number.isInteger(n) && n >= lo && n <= hi;
};

const Row: React.FC<{
  label: string;
  help: string;
  unit: string;
  value: string;
  range: string;
  ok: boolean;
  disabled: boolean;
  onChange: (v: string) => void;
}> = ({ label, help, unit, value, range, ok, disabled, onChange }) => (
  <div className="grid gap-2 border-b border-gray-50 py-4 last:border-0 md:grid-cols-[1fr_auto] md:items-center">
    <div>
      <p className="text-sm font-medium text-gray-900">{label}</p>
      <p className="text-sm text-gray-500">{help}</p>
    </div>
    <div className="flex items-center gap-2">
      <input
        type="number"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={`${INPUT_CLASS} w-28 ${ok ? '' : 'border-red-300'}`}
      />
      <span className="w-20 text-sm text-gray-500">{unit}</span>
      <span className="hidden w-24 text-xs text-gray-400 lg:block">{range}</span>
    </div>
  </div>
);

const VoiceSettingsPage: React.FC = () => {
  useBreadcrumbs([{ label: 'Voice calling', href: VOICE_BASE }, { label: 'Settings' }]);
  const perms = useVoicePerms();
  const refresh = useVoiceRefresh();
  const q = useVoiceSettingsQuery();
  const m = useVoiceSettingsUpdateMutation();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirm, setConfirm] = useState(false);
  const s = q.data;

  if (perms.loading || !perms.canView) {
    return (
      <PageShell title="Settings" description="">
        {perms.loading ? <Skeleton className="h-64 w-full" /> : <AccessDenied />}
      </PageShell>
    );
  }
  const d = draft ?? (s ? toDraft(s) : null);
  const ok = !!d && inRange(d.hold, 60, 900) && inRange(d.ads, 0, 100) && inRange(d.max, 1, 120);
  const dirty = !!s && !!d && JSON.stringify(d) !== JSON.stringify(toDraft(s));
  const set = (k: keyof Draft) => (v: string) => d && setDraft({ ...d, [k]: v });

  return (
    <PageShell
      title="Settings"
      description="Rules every call follows. Boxes pick up a change within a minute."
      actions={
        <div className="flex items-center gap-2">
          <FetchingChip />
          <Button variant="outline" size="sm" onClick={() => refresh()}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" /> Refresh
          </Button>
        </div>
      }
    >
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.isLoading || !s || !d ? (
        !q.isError && <LoadingNote>Loading the voice calling settings.</LoadingNote>
      ) : (
        <Card
          title="Call rules"
          note={s.updated_at ? `Last changed ${fmtWhen(s.updated_at)}${s.updated_by ? ` by ${s.updated_by}` : ''}` : undefined}
        >
          <Row label="Hold time" help="How long the number stays open for the buyer to call after they ask for it." unit="seconds" range="60 to 900" value={d.hold} ok={inRange(d.hold, 60, 900)} disabled={!perms.canManage} onChange={set('hold')} />
          <Row label="Ad frequency" help="Share of calls that hear an ad before the welcome. 0 turns ads off." unit="% of calls" range="0 to 100" value={d.ads} ok={inRange(d.ads, 0, 100)} disabled={!perms.canManage} onChange={set('ads')} />
          <Row label="Longest call" help="A call that reaches this length is ended." unit="minutes" range="1 to 120" value={d.max} ok={inRange(d.max, 1, 120)} disabled={!perms.canManage} onChange={set('max')} />
          {perms.canManage ? (
            <div className="mt-4 flex gap-2">
              <Button disabled={!dirty || !ok} onClick={() => setConfirm(true)}>Save changes</Button>
              <Button variant="outline" disabled={!dirty} onClick={() => setDraft(null)}>Discard</Button>
            </div>
          ) : (
            <p className="mt-4 text-xs text-gray-500">Changing these needs the voice.manage permission.</p>
          )}
        </Card>
      )}
      {confirm && d && (
        <FormDialog
          title="Save voice calling settings"
          description="New calls follow these rules straight away. Calls already in progress are not changed."
          confirmLabel="Save"
          busy={m.isPending}
          error={m.error}
          onClose={() => setConfirm(false)}
          onConfirm={(reason) =>
            m.mutate(
              {
                reason,
                settings: { hold_seconds: Number(d.hold), ad_frequency_percent: Number(d.ads), max_call_minutes: Number(d.max) },
              },
              { onSuccess: () => { toast.success('Settings saved'); setDraft(null); setConfirm(false); } },
            )
          }
        >
          <ul className="space-y-1 text-sm text-gray-700">
            <li>Hold time: {s?.hold_seconds} to {d.hold} seconds</li>
            <li>Ad frequency: {s?.ad_frequency_percent} to {d.ads}%</li>
            <li>Longest call: {s?.max_call_minutes} to {d.max} minutes</li>
          </ul>
        </FormDialog>
      )}
    </PageShell>
  );
};

export default VoiceSettingsPage;
